import React from "react";
import Api from "../../../utils/Api";
import ChildrenCentricPage from "../../../modules/ChildrenCentricPage";
import PortalHoc from "../../../modules/PortalHoc";
import TableLeftPanel from "../../../utils/TableLeftPanel";
import { Tooltip, Text } from "@chakra-ui/react";
import { QuestionIcon } from "@chakra-ui/icons";
import { Link } from "@familyzone/component-library";
import { useDateRangeFilterStore } from "../../../storez/DateRangeFilterStore";
import { useDeviceOptionsStore } from "../../../storez/DeviceOptionsStore";
import GlobalDatePickerVisibilityActions from "../../../actions/GlobalDatePickerVisibilityActions";
import DiagnosticsMetricsAdvancedSelector from "./DiagnosticsMetricsAdvancedSelector";
import DiagnosticsMetricsAdvancedChart from "./DiagnosticsMetricsAdvancedChart";
import DiagnosticsMetricsAdvancedTable from "./DiagnosticsMetricsAdvancedTable";

const DiagnosticsMetricsAdvanced: React.FC = () => {
  interface OnChange {
    value: string;
  }

  interface Item {
    value: string;
    text?: string | undefined;
  }

  interface Version {
    uptime: number;
    version: string;
  }

  interface MetricSample {
    time: number;
    [key: string]: number;
  }

  interface MetricData {
    data: MetricSample[];
  }

  interface MetricDataSet {
    deviceId: string;
    data: MetricSample[];
  }

  const [dynamic_metric, setDynamicMetric] = React.useState<string>("system.utime");
  const [dynamic_metric_data, setDynamicMetricData] = React.useState<MetricDataSet[]>([]);
  const [sphireos_version, setSphireosVersion] = React.useState<number>(270.0);
  const [device_ids, setDeviceIds] = React.useState<Item[]>([]);
  const [selected_device_ids, setSelectedDeviceIds] = React.useState<Item[]>([]);
  const [loaded, setLoaded] = React.useState<boolean>(false);

  const [deviceId, isParent] = useDeviceOptionsStore((state) => [state.options?.deviceid, state.options?.is_parent] as const);

  const handle_load_metric = React.useCallback(() => {
    setDynamicMetricData([]);
    setLoaded(false);
    selected_device_ids.forEach((dev) => {
      Api.get_analytics(
        "/managedevice/ajax/device/metric/" + dynamic_metric,
        (result: MetricData) => {
          setDynamicMetricData((prev) => [...prev, { deviceId: dev["value"], data: result["data"] }]);
          setLoaded(true);
        },
        (err: Error) => {
          console.log(err);
        },
        {},
        dev["value"]
      );
    });
  }, [dynamic_metric, selected_device_ids]);

  const onDateChanged = () => {
    handle_load_metric();
  };

  React.useEffect(() => {
    const unsubFilterStore = useDateRangeFilterStore.subscribe(onDateChanged);
    setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      GlobalDatePickerVisibilityActions.showGlobalDatePicker();
    }, 0);

    return () => {
      unsubFilterStore();
    };
  });

  React.useEffect(() => {
    const match = window.location.search.match(/[?&]startingMetric=([^&]+)/);
    if (match) {
      const startingMetric = match[1] ?? "system.utime";
      setDynamicMetric(startingMetric);
    }

    const deviceIds = [{ value: deviceId ?? "", text: deviceId }];
    if (isParent) {
      Api.get("/managedevice/ajax/device/children", (data: { device_ids: string[] }) => {
        data["device_ids"].forEach((dev: string) => {
          deviceIds.push({ value: dev, text: dev });
        });
        setDeviceIds(deviceIds);
        setSelectedDeviceIds([deviceIds[0]]);
      });
    } else {
      setDeviceIds(deviceIds);
      setSelectedDeviceIds(deviceIds);
    }
  }, [isParent, deviceId]);

  React.useEffect(() => {
    Api.get("/managedevice/ajax/device/update/version", function (result: Version) {
      setSphireosVersion(parseFloat(result["version"]));
    });
  }, []);

  React.useEffect(() => {
    handle_load_metric();
  }, [dynamic_metric, selected_device_ids, handle_load_metric]);

  const handle_MetricChange = (event: OnChange) => {
    setDynamicMetric(event.value);
    setLoaded(false);
  };

  const handle_SelectAll = () => {
    setSelectedDeviceIds(device_ids);
  };

  const handle_SetSelectedDevices = (dev: Item[]) => {
    setSelectedDeviceIds(dev);
  };

  const render__buttons = () => {
    return <div />;
  };

  return (
    <TableLeftPanel>
      <ChildrenCentricPage icon="fa fa-cog" title="Performance Metrics" buttons={render__buttons()}>
        <PortalHoc elementId="fz_table_header">
          <Tooltip variant="dark" hasArrow={true} maxWidth="336px" fontSize="md" label="Click for help">
            <Link
              isExternal
              href={"https://help.linewize.com/hc/en-gb/articles/12980554058012-School-Manager-Performance-Metrics"}
              color="currentColor"
              order={2}
            >
              <QuestionIcon boxSize={6} ml={7} />
            </Link>
          </Tooltip>
        </PortalHoc>
        <DiagnosticsMetricsAdvancedSelector
          dynamic_metric={dynamic_metric}
          handle_MetricChange={handle_MetricChange}
          handle_SelectAll={handle_SelectAll}
          sphireos_version={sphireos_version}
          is_parent={isParent ?? false}
          device_ids={device_ids}
          selected_device_ids={selected_device_ids}
          handle_SetSelectedDevices={handle_SetSelectedDevices}
        />
        {dynamic_metric_data.map((device) => (
          <DiagnosticsMetricsAdvancedChart
            deviceId={device.deviceId}
            metric_data={device.data}
            dynamic_metric={dynamic_metric}
            loaded={loaded}
          />
        ))}
        {dynamic_metric_data.length > 0 ? (
          <>
            <Text aria-level={2} fontFamily="paragraphs" fontSize="md" color="text.title" mb="sp4" role="heading">
              {dynamic_metric}
            </Text>
            <DiagnosticsMetricsAdvancedTable
              dynamic_metric={dynamic_metric}
              metric_data={dynamic_metric_data}
              selected_device_ids={selected_device_ids}
              loaded={loaded}
            />
          </>
        ) : (
          ""
        )}
      </ChildrenCentricPage>
    </TableLeftPanel>
  );
};

export default DiagnosticsMetricsAdvanced;
